// AddAccountModal.jsx — إضافة / تعديل حساب (بالتصميم الموحّد)
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, TextField, MenuItem, Button, IconButton,
  Autocomplete, ToggleButton, ToggleButtonGroup, Switch, FormControlLabel, CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { toast } from 'react-toastify';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';
import { fieldSx, BRAND } from '../../../components/Reports/ReportKit';

export const AccountNature = [{ id: 1, name: 'مدين' }, { id: 2, name: 'دائن' }];
export const AccountTypes = [
  { id: 1, name: 'أصول' }, { id: 2, name: 'خصوم' }, { id: 3, name: 'حقوق ملكية' },
  { id: 4, name: 'إيرادات' }, { id: 5, name: 'مصروفات' },
];
const FinalAccountTypes = [
  { id: 1, name: 'ميزانية' }, { id: 2, name: 'قائمة الدخل' }, { id: 3, name: 'حساب مخزون' },
  { id: 4, name: 'ضريبة مشتريات' }, { id: 5, name: 'ضريبة مبيعات' }, { id: 6, name: 'مبيعات' },
  { id: 7, name: 'مردود مبيعات' }, { id: 8, name: 'خصم' },
];
const TaxTypes = [{ id: 0, name: 'بدون ضريبة' }, { id: 1, name: 'ضريبة قيمة مضافة' }, { id: 2, name: 'ضريبة استقطاع' }];
const MAX_LEVEL = 5;

const emptyForm = {
  parentId: '', code: '', nameAr: '', nameEn: '', nature: 1, accountType: 1, finalAccountType: '',
  taxType: 0, costCenterRequired: false, description: '', branchId: '', allowPosting: true, isActive: true,
};

// القيمة المنطقية من الـ API (isActive / IsActive) — الغائبة تعني نشط
const boolOf = (...vals) => { for (const v of vals) if (typeof v === 'boolean') return v; return true; };

export default function AddAccountModal({ isOpen, onClose, onSuccess, initialParentId = '', initialAccount = null }) {
  const isEdit = Boolean(initialAccount?.id);
  const [form, setForm] = useState(emptyForm);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (name, value) => setForm(f => ({ ...f, [name]: value }));

  // ── الحسابات (لاختيار الأب) ──
  useEffect(() => {
    if (!isOpen) return;
    chartOfAccountsApi.getAll()
      .then(res => setAll(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error('تعذر تحميل الحسابات'));
  }, [isOpen]);

  // الأب المسموح: ليس من المستوى الأخير، وفي التعديل ليس الحساب نفسه ولا أحد فروعه
  const parents = useMemo(() => {
    const ownCode = isEdit ? String(form.code || initialAccount?.code || '') : null;
    return all.filter(a => a.level < MAX_LEVEL && (!isEdit || (
      String(a.id) !== String(initialAccount.id) && !(ownCode && String(a.code).startsWith(ownCode) && a.code !== ownCode)
    )));
  }, [all, isEdit, initialAccount, form.code]);
  const parent = parents.find(p => String(p.id) === String(form.parentId)) || null;

  // ── فتح النافذة: جديد أو تعديل ──
  useEffect(() => {
    if (!isOpen) return;
    if (!isEdit) { setForm(emptyForm); return; }
    setLoading(true);
    chartOfAccountsApi.getById(initialAccount.id)
      .then(res => {
        const d = res.data || {};
        setForm({
          parentId: d.parentId ?? '',
          code: d.code ?? '',
          nameAr: d.nameAr ?? '',
          nameEn: d.nameEn ?? '',
          nature: d.nature ?? 1,
          accountType: d.accountType ?? 1,
          finalAccountType: d.finalAccountType ?? '',
          taxType: d.taxType ?? 0,
          costCenterRequired: !!d.costCenterRequired,
          description: d.description ?? '',
          branchId: d.branchId ?? '',
          allowPosting: typeof d.allowPosting === 'boolean' ? d.allowPosting : true,
          // ✅ كان يُهمل فيُحفظ الحساب "غير نشط" بمجرد التعديل
          isActive: boolOf(d.isActive, d.IsActive, initialAccount?.isActive),
        });
      })
      .catch(() => { toast.error('تعذر تحميل بيانات الحساب للتعديل'); onClose?.(); })
      .finally(() => setLoading(false));
  }, [isOpen, isEdit, initialAccount?.id]);

  // ── الأب المحدد مسبقًا (إضافة تحت الحساب المحدد في الشجرة) ──
  useEffect(() => {
    if (!isOpen || isEdit || !initialParentId || !all.length) return;
    if (String(form.parentId) === String(initialParentId)) return;
    if (all.some(p => String(p.id) === String(initialParentId) && p.level < MAX_LEVEL)) changeParent(initialParentId);
  }, [isOpen, isEdit, initialParentId, all]);

  // اختيار الأب: يرث الطبيعة والنوع + الرقم التالي تلقائيًا
  const changeParent = (parentId) => {
    const p = all.find(a => String(a.id) === String(parentId));
    setForm(f => ({
      ...f, parentId: parentId ?? '',
      nature: p ? p.nature : f.nature,
      accountType: p ? p.accountType : f.accountType,
    }));
    if (!parentId) { if (!isEdit) set('code', ''); return; }
    chartOfAccountsApi.getNextCode(parentId)
      .then(res => { if (res.data?.code) set('code', res.data.code); })
      .catch(() => { if (p) set('code', p.code); });
  };

  const codeError = parent && form.code && !String(form.code).startsWith(String(parent.code))
    ? `يجب أن يبدأ برقم الحساب الرئيسي ${parent.code}` : '';

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!isEdit && !parent) { toast.error('يرجى اختيار الحساب الرئيسي'); return; }
    if (!String(form.code).trim()) { toast.error('رقم الحساب مطلوب'); return; }
    if (codeError) { toast.error(codeError); return; }
    if (!form.nameAr.trim()) { toast.error('الاسم بالعربي مطلوب'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        code: String(form.code).trim(),
        nameAr: form.nameAr.trim(),
        nameEn: form.nameEn.trim(),
        level: parent ? parent.level + 1 : (initialAccount?.level ?? 1),
        parentId: form.parentId || null,
        branchId: form.branchId || null,
        nature: Number(form.nature),
        accountType: Number(form.accountType),
        taxType: Number(form.taxType),
        finalAccountType: form.finalAccountType === '' ? null : Number(form.finalAccountType),
        isActive: !!form.isActive,
      };
      if (isEdit) {
        await chartOfAccountsApi.update(initialAccount.id, payload);
        toast.success('تم تعديل الحساب');
      } else {
        await chartOfAccountsApi.create(payload);
        toast.success('تم إضافة الحساب');
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      const d = err.response?.data;
      toast.error(d?.errors ? Object.values(d.errors).flat().join(' — ') : (d?.message || (typeof d === 'string' ? d : 'تعذر حفظ الحساب')));
    } finally {
      setSaving(false);
    }
  };

  const tbSx = {
    '& .MuiToggleButton-root': { flex: 1, py: 1, fontWeight: 800, borderRadius: '10px !important', border: '1px solid #e5e7eb !important', mx: .25, gap: .75 },
    '& .Mui-selected': { bgcolor: `${BRAND.primarySoft} !important`, color: `${BRAND.primary} !important`, borderColor: `${BRAND.primaryLight} !important` },
  };

  return (
    <Dialog open={!!isOpen} onClose={() => !saving && onClose?.()} dir="rtl" maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, color: '#fff', background: BRAND.headerGradient }}>
        <Box sx={{ width: 38, height: 38, borderRadius: '11px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.28)' }}>
          {isEdit ? <EditOutlinedIcon /> : <AccountTreeOutlinedIcon />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{isEdit ? 'تعديل حساب' : 'إضافة حساب جديد'}</Typography>
          <Typography sx={{ fontSize: 12.5, opacity: .85 }} noWrap>
            {isEdit ? `${initialAccount.code} — ${initialAccount.nameAr}` : parent ? `تحت: ${parent.code} — ${parent.nameAr}` : 'اختر الحساب الرئيسي'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent component="form" onSubmit={submit} sx={{ p: { xs: 2, sm: 2.5 }, display: 'grid', gap: 2, bgcolor: '#f8fafc' }}>
        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress sx={{ color: BRAND.primary }} /></Box>
        ) : (
          <>
            {/* الأب والرقم */}
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, pt: 1 }}>
              <Autocomplete
                options={parents}
                value={parent}
                onChange={(_, v) => changeParent(v?.id ?? '')}
                getOptionLabel={(o) => `${o.code} — ${o.nameAr}`}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pr: (o.level - 1) * 1.5 }}>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: BRAND.primary, fontSize: 13 }}>{o.code}</Typography>
                      <Typography sx={{ fontSize: 14 }}>{o.nameAr}</Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="لا توجد نتائج"
                renderInput={(params) => <TextField {...params} size="small" label={`الحساب الرئيسي (الأب)${isEdit ? '' : ' *'}`} sx={fieldSx} />}
              />
              <TextField size="small" label="رقم الحساب *" value={form.code} onChange={e => set('code', e.target.value.replace(/\s/g, ''))}
                placeholder="يُولَّد تلقائيًا" error={!!codeError} helperText={codeError || (parent ? `يبدأ بـ ${parent.code}` : ' ')}
                inputProps={{ dir: 'ltr', style: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 800 } }} sx={fieldSx} />
            </Box>

            {/* رئيسي / فرعي */}
            <Box>
              <ToggleButtonGroup exclusive fullWidth value={form.allowPosting ? 'sub' : 'main'} onChange={(_, v) => v && set('allowPosting', v === 'sub')} sx={tbSx}>
                <ToggleButton value="main"><FolderOutlinedIcon fontSize="small" /> حساب رئيسي (تجميعي)</ToggleButton>
                <ToggleButton value="sub"><DescriptionOutlinedIcon fontSize="small" /> حساب فرعي (عليه حركة)</ToggleButton>
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: 12, color: '#64748b', mt: .5 }}>
                {form.allowPosting ? 'يمكن القيد عليه مباشرة في القيود والفواتير والسندات.' : 'لا يُقيد عليه مباشرة — يجمع أرصدة الحسابات تحته.'}
              </Typography>
            </Box>

            {/* الأسماء */}
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField size="small" label="الاسم بالعربي *" value={form.nameAr} onChange={e => set('nameAr', e.target.value)} sx={fieldSx} autoFocus={!isEdit} />
              <TextField size="small" label="Name (English)" value={form.nameEn} onChange={e => set('nameEn', e.target.value)} inputProps={{ dir: 'ltr' }} sx={fieldSx} />
            </Box>

            {/* التصنيف */}
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' } }}>
              <TextField select size="small" label="طبيعة الحساب" value={form.nature} onChange={e => set('nature', e.target.value)} sx={fieldSx}>
                {AccountNature.map(n => <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="نوع الحساب" value={form.accountType} onChange={e => set('accountType', e.target.value)} sx={fieldSx}>
                {AccountTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="الحساب الختامي" value={form.finalAccountType} onChange={e => set('finalAccountType', e.target.value)} sx={fieldSx}>
                <MenuItem value="">— اختياري —</MenuItem>
                {FinalAccountTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="نوع الضريبة" value={form.taxType} onChange={e => set('taxType', e.target.value)} sx={fieldSx}>
                {TaxTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Box>

            {/* الخيارات */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {[
                ['isActive', 'الحساب نشط', 'الحساب غير النشط لا يظهر في الاختيارات'],
                ['costCenterRequired', 'يتطلب مركز تكلفة', 'يُلزم اختيار مركز تكلفة عند القيد عليه'],
              ].map(([k, l, hint]) => (
                <Box key={k} sx={{ flex: '1 1 220px', p: 1.25, borderRadius: '12px', bgcolor: '#fff', border: `1px solid ${form[k] ? BRAND.primaryLight : '#e5e7eb'}` }}>
                  <FormControlLabel sx={{ m: 0, width: '100%', justifyContent: 'space-between', flexDirection: 'row-reverse' }}
                    control={<Switch checked={!!form[k]} onChange={e => set(k, e.target.checked)} />}
                    label={<Box><Typography sx={{ fontWeight: 800, fontSize: 14 }}>{l}</Typography><Typography sx={{ fontSize: 12, color: '#64748b' }}>{hint}</Typography></Box>} />
                </Box>
              ))}
            </Box>
            {isEdit && !form.isActive && (
              <Alert severity="warning" sx={{ borderRadius: '12px', py: 0 }}>سيُحفظ الحساب <b>غير نشط</b>.</Alert>
            )}

            <TextField size="small" label="ملاحظات / وصف" value={form.description} onChange={e => set('description', e.target.value)} multiline minRows={2} sx={fieldSx} />
            <button type="submit" hidden />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, borderTop: '1px solid #eef0f4' }}>
        <Button onClick={onClose} disabled={saving} variant="text" sx={{ borderRadius: '10px', fontWeight: 700, color: '#64748b', bgcolor: 'transparent' }}>إلغاء</Button>
        <Button variant="contained" onClick={submit} disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
          sx={{ borderRadius: '10px', fontWeight: 800, px: 3, background: BRAND.buttonGradient, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>
          {saving ? 'جاري الحفظ...' : isEdit ? 'حفظ التعديلات' : 'حفظ الحساب'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}